import { FC } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Tag,
  Spin,
  Alert,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import { useBillingDashboard } from "~/services/billing/billing.queries";
import { Invoice } from "~/components/admin/billing/types";

const { RangePicker } = DatePicker;

export const ScreenBillingDashboard: FC = () => {
  // Use real API call instead of mock data
  const { data: dashboardData, isLoading, error } = useBillingDashboard();

  if (error) {
    return (
      <div>
        <PageMeta
          title="Billing Dashboard | Art Handler"
          description="Financial overview and billing metrics"
        />
        <BillingSubNav activeKey="dashboard" />
        <Alert
          message="Error Loading Dashboard"
          description="Failed to load billing dashboard data. Please check your connection to the backend."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (invoiceNumber: string, record: Invoice) =>
        invoiceNumber || record.id,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => `$${amount?.toFixed(2) || "0.00"}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Invoice["status"]) => {
        const statusConfig: Record<string, { color: string; label: string }> = {
          draft: { color: "blue", label: "Draft" },
          pending: { color: "orange", label: "Pending" },
          sent: { color: "cyan", label: "Sent" },
          paid: { color: "green", label: "Paid" },
          overdue: { color: "red", label: "Overdue" },
          cancelled: { color: "gray", label: "Cancelled" },
          not_invoiced: { color: "blue", label: "Not Invoiced" },
          invoiced: { color: "orange", label: "Invoiced" },
        };
        const config = statusConfig[status] || {
          color: "default",
          label: status,
        };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "N/A",
    },
  ];

  return (
    <div>
      <PageMeta
        title="Billing Dashboard | Art Handler"
        description="Financial overview and billing metrics"
      />

      <BillingSubNav activeKey="dashboard" />

      {/* Date Range Filter */}
      <Card className="mb-6 shadow-sm">
        <div className="flex justify-end">
          <RangePicker />
        </div>
      </Card>

      <Spin spinning={isLoading}>
        {/* Statistics Cards */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Outstanding"
                value={dashboardData?.totalOutstanding || 0}
                precision={2}
                valueStyle={{ color: "#cf1322" }}
                prefix={<DollarOutlined />}
                suffix=""
                formatter={(value) => `$${value}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Pending Invoices"
                value={dashboardData?.totalPending || 0}
                precision={2}
                valueStyle={{ color: "#faad14" }}
                prefix={<DollarOutlined />}
                suffix=""
                formatter={(value) => `$${value}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Paid (Month)"
                value={dashboardData?.totalPaid || 0}
                precision={2}
                valueStyle={{ color: "#3f8600" }}
                prefix={<DollarOutlined />}
                suffix={<ArrowUpOutlined />}
                formatter={(value) => `$${value}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Invoices"
                value={dashboardData?.totalInvoices || 0}
                valueStyle={{ color: "#1890ff" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Recent Invoices */}
        <Card title="Recent Invoices" className="shadow-sm">
          <Table
            columns={columns}
            dataSource={dashboardData?.recentInvoices || []}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            locale={{
              emptyText: isLoading
                ? "Loading..."
                : "No invoices found. Connect to backend to load real data.",
            }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default ScreenBillingDashboard;
